import React, { useState, useEffect, FC } from "react";
import { Link, Redirect, Route, Switch, useRouteMatch, useLocation } from "react-router-dom";
import { map } from "lodash";
import AppHeader from "../../components/AppHeader";
import { ActiveWorkflow, ArchivedWorkflow, HistoryWorkflow } from "../../components/Workflow";
import APTRequests from "../../components/AppointmentRequestTable";
import Settings from "../../components/Settings";
import MyTodoList from "../../components/MyTodoList";
import Specialists from "../../components/Specialists";
import LiveAppointments from "../../components/LiveAppointments";
import ReferralInbox from "../ReferralInbox";
import ErrorResultsInbox from "../ErrorResultsInbox";
import ErrorReportsInbox from "../ErrorReportsInbox";
import Schedule from "../Schedule";
import {
  isLiveAppointmentsEnabled,
  isReferralInboxEnabled,
  isAIReferralInboxEnabled,
  isAIResultInboxEnabled,
  isGPReportUploadEnabled,
  isPmsBookingEnabled,
} from "../../infra/featureToggle";
import { getToggleEnabled, TOGGLES } from "../../toggles";
import { tabs, GLOBAL_SECURE_FILES_ROLES } from "../../constants";
import GlobalSecureFiles from "../../components/GlobalSecureFiles";
import { useSelector } from "react-redux";

// SE-745 migration check page; temporary, and its route exists only outside production.
const MigrationCheck = React.lazy(() => import("../MigrationCheck"));
const isMigrationCheckEnabled = process.env.REACT_APP_ENVIRONMENT !== "production";

const Dashboard: FC<{ handleSignOut: () => void }> = ({ handleSignOut }) => {
  const isSecureFilesEnabled = getToggleEnabled(TOGGLES.SECURE_FILES);
  const currentUser = useSelector((state: { user?: { currentUser?: { role?: string } } }) => state.user?.currentUser);
  const canAccessGlobalSecureFiles =
    isSecureFilesEnabled && GLOBAL_SECURE_FILES_ROLES.includes(currentUser?.role ?? "");
  const { path, url } = useRouteMatch();
  const [selectedTabId, setSelectedTabId] = useState("db4_mailer");
  const location: { state?: { isResetPassword?: boolean }; pathname?: string } = useLocation();

  useEffect(() => {
    if (!location || !location.pathname) {
      return;
    }
    const [, , path] = location.pathname.split("/");
    if (!path) {
      return;
    }
    const tab = tabs.find((t) => t.path === path);
    if (!tab || !tab.id) {
      return;
    }
    setSelectedTabId(tab.id);
  }, [location]);

  const handleSelectTab = (tabId: string) => {
    setSelectedTabId(tabId);
  };

  return (
    <>
      <AppHeader signOut={handleSignOut} />
      <div className="tabs is-toggle">
        <ul>
          {map(tabs, (tab) => {
            if (tab.id === "live_appointments" && !isLiveAppointmentsEnabled) return;
            if (tab.id === "schedule" && isPmsBookingEnabled) return;
            if (tab.id === "referral_inbox" && !isReferralInboxEnabled) return;
            if (tab.id === "error_reports_inbox" && !isGPReportUploadEnabled) return;
            if (tab.id === "global_secure_files" && !isSecureFilesEnabled) return null;
            if (tab.permittedRoles && !tab.permittedRoles.includes(currentUser?.role ?? "")) {
              return null;
            }
            return (
              <li
                key={tab.id}
                className={`${selectedTabId === tab.id ? "is-active " : ""}is-size-7`}
                onClick={(e) => {
                  e.preventDefault();
                  handleSelectTab(tab.id);
                }}
              >
                <Link to={`${url}/${tab.path}`}>{tab.label}</Link>
              </li>
            );
          })}
        </ul>
      </div>

      <Switch>
        <Route exact path={path}>
          <Redirect to={`${path}/client-appointment`} />
        </Route>
        <Route path={`${path}/client-appointment`}>
          <ActiveWorkflow />
        </Route>
        <Route path={`${path}/archived-client-appointment`}>
          <ArchivedWorkflow />
        </Route>
        <Route path={`${path}/history-client-appointment`}>
          <HistoryWorkflow />
        </Route>
        <Route path={`${path}/settings`}>
          <Settings />
        </Route>
        <Route path={`${path}/my-todo-list`}>
          <MyTodoList />
        </Route>
        <Route path={`${path}/apt-requests`}>
          <APTRequests />
        </Route>
        {!isPmsBookingEnabled ? (
          <Route path={`${path}/schedule`}>
            <Schedule />
          </Route>
        ) : null}
        <Route path={`${path}/specialists`}>
          <Specialists />
        </Route>
        {isLiveAppointmentsEnabled ? (
          <Route path={`${path}/live-appointments`}>
            <LiveAppointments />
          </Route>
        ) : null}
        {isReferralInboxEnabled && (
          <Route path={`${path}/referral-inbox`}>
            <ReferralInbox source={isAIReferralInboxEnabled ? "ai_extracted_referrals" : "ocr_clients"} />
          </Route>
        )}
        <Route path={`${path}/error-results-inbox`}>
          <ErrorResultsInbox aiOcr={isAIResultInboxEnabled} />
        </Route>
        <Route path={`${path}/error-reports-inbox`}>
          <ErrorReportsInbox />
        </Route>
        {canAccessGlobalSecureFiles ? (
          <Route path={`${path}/secure-files`}>
            <GlobalSecureFiles />
          </Route>
        ) : null}
        {isMigrationCheckEnabled ? (
          <Route path={`${path}/migration-check`}>
            <React.Suspense fallback={null}>
              <MigrationCheck />
            </React.Suspense>
          </Route>
        ) : null}
      </Switch>
    </>
  );
};

export default Dashboard;
